import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ASSETS, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { bootstrapFromToken } = useAuth();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!token) {
    return (
      <SimpleShell>
        <div className="text-center text-sm text-black/70" data-testid="reset-invalid">
          This reset link is invalid or expired.{" "}
          <Link to="/forgot-password" className="text-[var(--trio-primary)] underline">
            Request a new one.
          </Link>
        </div>
      </SimpleShell>
    );
  }

  async function submit(e) {
    e.preventDefault();
    if (submitting) return;
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password,
      });
      localStorage.setItem("trio_admin_token", data.access_token);
      localStorage.setItem("trio_admin_user", JSON.stringify(data.user));
      toast.success("Password reset — you're signed in.");
      if (typeof bootstrapFromToken === "function") await bootstrapFromToken();
      navigate(data.user?.role === "staff" ? "/staff" : "/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't reset password");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <SimpleShell>
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <div>
          <label className="text-xs tracking-widest uppercase text-black/70 font-semibold">
            New Password
          </label>
          <div className="relative mt-1">
            <input
              type={showPassword ? "text" : "password"}
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="reset-new-password"
              className="w-full bg-white border border-black/25 focus:border-[var(--trio-primary)] px-4 py-3 pr-11 text-black focus:outline-none rounded"
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              data-testid="reset-toggle-visibility"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-black/40 hover:text-black/70"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <div className="text-[10px] text-black/40 mt-1">At least 8 characters</div>
        </div>

        <div>
          <label className="text-xs tracking-widest uppercase text-black/70 font-semibold">
            Confirm New Password
          </label>
          <input
            type={showPassword ? "text" : "password"}
            required
            minLength={8}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="reset-confirm-password"
            className="w-full mt-1 bg-white border border-black/25 focus:border-[var(--trio-primary)] px-4 py-3 text-black focus:outline-none rounded"
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          data-testid="reset-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white py-3 text-sm tracking-widest uppercase font-bold disabled:opacity-60 rounded"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Reset Password
        </button>
      </form>

      <div className="mt-6 text-center">
        <Link to="/staff/login" className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase text-black/40 hover:text-black/70">
          <ArrowLeft className="w-3 h-3" /> Back to sign in
        </Link>
      </div>
    </SimpleShell>
  );
}

function SimpleShell({ children }) {
  return (
    <div className="min-h-screen bg-[var(--trio-bg)] text-black flex flex-col items-center justify-center px-6 py-10">
      <div className="w-full max-w-md bg-white border border-black/10 shadow-xl rounded-lg p-8">
        <div className="text-center mb-6">
          <img src={ASSETS.logo} alt="Trio" className="h-14 w-auto mx-auto mb-4" />
          <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold">
            Set a New Password
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}
